import { BookCard } from "components/book-card";
import { RenderContent } from "components/content-renderer";
import { copyTextToClipboard } from "lib/copy-to-clipboard";
import { genMd5HashForEventSlug } from "lib/crypto";
import {
  GetEventQuery,
  Roles_Enum,
  useGetEventConfLocationQuery,
} from "lib/graphql-generated";
import { useUser } from "lib/hooks/use-user";
import { DateObject } from "lib/utils";
import Link from "next/link";
import { useState } from "react";
import { Chat } from "./chat";
import { BookClubTicketSection } from "./tickets/bookclub-ticket-section";
import { EventTicketSection } from "./tickets/event-ticket-section";

interface Props {
  event: GetEventQuery["events"][number];
  showConference: boolean;
}
export const EventPage = ({ event, showConference }: Props) => {
  const { userData } = useUser();
  const [tab, setTab] = useState<"about" | "chat">("about");

  const {
    data: {
      events_by_pk: { video_conf_location },
    },
  } = useGetEventConfLocationQuery({
    variables: {
      id: event.id,
    },
  });

  const isPastEvent =
    new Date(Date.now()) >
    new Date(new Date(event.start_time).getTime() - 1000 * 60 * 5); // five minutes before the event

  const date = new Date(event.start_time);
  const startTime = DateObject(event.start_time);
  const endTime = DateObject(event.end_time);

  return (
    <>
      <article
        className={`${
          event.is_book_club ? "bg-blue-default " : "bg-yellow-default"
        } p-4 md:p-12
        lg:p-16 max-w-screen-xl mx-auto mb-12`}>
        <div
          className={`md:border-8 md:p-8 lg:p-12 ${
            event.is_book_club ? "border-blue-light" : "border-yellow-light"
          } rounded-lg md:grid grid-cols-3 md:gap-x-8 lg:gap-x-12`}>
          <header className='flex mt-8 md:mt-0 flex-col col-span-full'>
            <span className='text-lg lg:text-2xl'>
              {!!event.festival
                ? "Festival Event"
                : event.is_online_only
                ? "Online Event"
                : event.is_book_club
                ? "Book Club"
                : "Event"}
            </span>
            {!!event.festival && (
              <Link href={`/events/${event.festival.slug}`}>
                <a className='mb-10 md:mb-2 text-orange-900 hover:underline'>
                  This event is a part of {event.festival.title} series. Click
                  to view more from this festival.
                </a>
              </Link>
            )}
            <h1 className='header text-5xl lg:text-7xl'>{event.title}</h1>
            <section className=' mb-6 space-y-2'>
              <dl className='flex flex-col text-xl'>
                {!isPastEvent && (
                  <>
                    <div className='flex flex-row space-x-2  text-xl'>
                      <dt className='mb-auto md:mt-auto header w-1/4 md:w-auto'>
                        Time:
                      </dt>
                      <dd className='mb-1.5 w-3/4 md:w-auto'>
                        {date.toLocaleString("en-GB", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}{" "}
                        : {startTime.hour}:{startTime.minute} - {endTime.hour}:
                        {endTime.minute}
                      </dd>
                    </div>
                    {event.location && (
                      <div className='flex flex-row space-x-2 text-xl'>
                        <dt className='mb-auto md:mt-auto header w-1/4 md:w-auto'>
                          Location:
                        </dt>
                        <dd className='mb-1.5 h-auto w-3/4 md:w-auto'>
                          {event.location}
                        </dd>
                      </div>
                    )}
                  </>
                )}
                {userData?.role === Roles_Enum.Bookseller &&
                  video_conf_location && (
                    <div className='flex flex-row space-x-2  w-max text-xl'>
                      <dt className='m-auto header'>Video Conference Link:</dt>
                      <dd className='mb-1.5'>
                        <button
                          className='bg-white px-2 py-0.5 rounded-md border-black border hover:bg-yellow-light flex flex-row space-x-2'
                          type='button'
                          onClick={() =>
                            copyTextToClipboard(
                              `${window.location.host}${
                                window.location.pathname
                              }?video_conf_access=${genMd5HashForEventSlug(
                                event
                              )}`
                            )
                          }>
                          <span className='text-sm'>Copy</span>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className='h-4 w-4 my-auto'
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'>
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3'
                            />
                          </svg>
                        </button>
                      </dd>
                    </div>
                  )}
              </dl>
            </section>
          </header>
          <section className='col-span-full mt-8'>
            {isPastEvent && showConference ? (
              <div
                className='w-full flex justify-center aspect-w-16 aspect-h-9'
                dangerouslySetInnerHTML={{
                  __html: video_conf_location,
                }}></div>
            ) : isPastEvent && event.video && event.video?.trim() !== "" ? (
              <div
                dangerouslySetInnerHTML={{ __html: event.video }}
                className='w-full flex justify-center aspect-w-16 aspect-h-9'></div>
            ) : (
              <img
                src={event.image}
                alt={`image for event: ${event.title}`}
                className='w-full mt-auto'
              />
            )}
          </section>

          {!isPastEvent &&
            (event.is_book_club ? (
              <BookClubTicketSection event={event} />
            ) : (
              <EventTicketSection event={event} />
            ))}
          <section
            className={`flex flex-col col-span-full mt-16 ${
              event.is_book_club ? "bg-blue-light " : "bg-yellow-light"
            }`}>
            {!event.is_book_club && (
              <nav className='w-full '>
                <ul className={`flex flex-row bg-yellow-default`}>
                  <li className='flex-1'>
                    <button
                      className={`w-full text-center hover:bg-yellow-light py-4 rounded-t-3xl cursor-pointer ${
                        tab === "about" ? "bg-yellow-light " : ""
                      }`}
                      onClick={() => setTab("about")}>
                      About
                    </button>
                  </li>
                  <li className='flex-1'>
                    <button
                      className={`w-full text-center hover:bg-yellow-light py-4 rounded-t-3xl cursor-pointer ${
                        tab === "chat" ? "bg-yellow-light" : ""
                      }`}
                      onClick={() => setTab("chat")}>
                      Chat
                    </button>
                  </li>
                </ul>
              </nav>
            )}
            {tab === "about" ? (
              <article className='pt-16'>
                {!event.is_book_club && (
                  <>
                    <hr className={` w-20 mx-auto  mb-2 `} />
                    <h2 className='header text-lg text-center'>
                      Featured Speakers
                    </h2>
                    <p className='capitalize  text-center'>{event.speakers}</p>
                    <hr className={` w-20 mx-auto  mt-2 mb-12`} />
                  </>
                )}
                <div className='mx-auto flex flex-col  px-2'>
                  {!!event.festival && (
                    <Link href={`/events/${event.festival.slug}`}>
                      <a className='w-prose mx-auto mb-2 text-orange-900 hover:underline'>
                        This event is a part of {event.festival.title} series.
                        Click to view more from this festival.
                      </a>
                    </Link>
                  )}
                  <div className='prose mx-auto'>
                    <RenderContent content={event.description} />
                  </div>
                </div>
              </article>
            ) : (
              <section className='md:p-8'>
                <Chat eventId={event.id} enable={event.enable_comments} />
              </section>
            )}
          </section>

          <section className='col-span-full mt-8'>
            <h2 className='header text-lg'>Featured Books</h2>
            <ul className='grid grid-cols-2 xs:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 md:gap-10 lg:gap-14'>
              {event.event_books.map(({ book }) =>
                book ? (
                  <BookCard key={book.isbn} book={book} invertButton />
                ) : null
              )}
            </ul>
          </section>
        </div>
      </article>
    </>
  );
};
